import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import '@/App.css';
import { Toaster } from '@/components/ui/sonner';
import { toast } from 'sonner';
import { ThemeProvider } from './contexts/ThemeContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { useSSOHandler } from './hooks/useSSOHandler';

import PasswordProtection, { checkPreviewAccess } from './pages/PasswordProtection';
import LandingPage from './pages/LandingPage';
import LicenseAgreement from './pages/LicenseAgreement';
import SalesPitch from './pages/SalesPitch';
import SupportAdminPortal from './pages/SupportAdminPortal';
import DocumentationPage from './pages/DocumentationPage';
import LoginPage from './pages/LoginPage';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import CompanyOnboarding from './pages/CompanyOnboarding';
import ActivateAccount from './pages/ActivateAccount';
import AuthCallback from './pages/AuthCallback';
import VerifyEmail from './pages/VerifyEmail';
import EmailVerified from './pages/EmailVerified';
import DashboardLayout from './components/DashboardLayout';
import Dashboard from './pages/Dashboard';
import Employees from './pages/Employees';
import EmployeeDetails from './pages/EmployeeDetails';
import EmployeeEdit from './pages/EmployeeEdit';
import LeaveManagement from './pages/LeaveManagement';
import Attendance from './pages/Attendance';
import Claims from './pages/Claims';
import Payroll from './pages/Payroll';
import PayrollDetail from './pages/PayrollDetail';
import Performance from './pages/Performance';
import Reports from './pages/Reports';
import Settings from './pages/Settings';
import Forms from './pages/Forms';
import Payslips from './pages/Payslips';
import MyProfile from './pages/MyProfile';
import EmployeeSettings from './pages/EmployeeSettings';
import MyTeam from './pages/MyTeam';
import ImportData from './pages/ImportData';
import PublicSign from './pages/PublicSign';
import Documents from './pages/Documents';
import BrandingSettings from './pages/BrandingSettings';
import LicenseSupport from './pages/LicenseSupport';
import LicenseAdmin from './pages/LicenseAdmin';
import LicenseManagement from './pages/LicenseManagement';
import AIConfiguration from './pages/AIConfiguration';
import DataMigration from './pages/DataMigration';
import PSCLAdmin from './pages/PSCLAdmin';
import HRSupportChat from './pages/HRSupportChat';
import HRInbox from './pages/HRInbox';
import OvertimeManagement from './pages/OvertimeManagement';
import ProbationManagement from './pages/ProbationManagement';
import TrainingManagement from './pages/TrainingManagement';
// Training is paused for HRMS v1 — route renders TrainingComingSoon below.
import TrainingComingSoon from './pages/TrainingComingSoon';
import OrganizationChart from './pages/OrganizationChart';
// Iter49 — full notifications page, destination of the bell's "View all" button.
import Notifications from './pages/Notifications';
// Org Chart is paused for HRMS v1 — route renders OrgChartComingSoon below.
import OrgChartComingSoon from './pages/OrgChartComingSoon';
import MyDocuments from './pages/MyDocuments';
import ChatbotWidget from './components/ChatbotWidget';
import SupportRenewalBanner from './components/SupportRenewalBanner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// TC-AUTH-033: Helper to get auth data from either localStorage or sessionStorage
const getAuthStorage = () => {
  // Check localStorage first (Remember Me enabled), then sessionStorage
  const token = localStorage.getItem('hrms_token') || sessionStorage.getItem('hrms_token');
  const user = localStorage.getItem('hrms_user') || sessionStorage.getItem('hrms_user');
  return { token, user };
};

// TC-AUTH-033: Helper to clear auth from both storages
const clearAuthStorage = () => {
  localStorage.removeItem('hrms_token');
  localStorage.removeItem('hrms_user');
  sessionStorage.removeItem('hrms_token');
  sessionStorage.removeItem('hrms_user');
};

// Setup axios defaults
axios.interceptors.request.use((config) => {
  const { token } = getAuthStorage();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Only redirect to login if we're not already on the login page
      // This prevents login form errors from causing a page refresh
      const isOnLoginPage = window.location.pathname === '/login' || window.location.pathname === '/signup';
      if (!isOnLoginPage) {
        clearAuthStorage();
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

function PrivateRoute({ children }) {
  const { token, user: storedUser } = getAuthStorage();
  
  if (!token) {
    return <Navigate to="/login" replace />;
  }
  
  // Check if email is verified (for Google OAuth users)
  if (storedUser) {
    try {
      const user = JSON.parse(storedUser);
      if (user.email_verified === false) {
        return <Navigate to="/verify-email" replace state={{ user }} />;
      }
    } catch (e) {
      // If parsing fails, proceed normally
    }
  }
  
  return children;
}

// Router wrapper to detect OAuth callback
function AppRouter({ user, setUser }) {
  const location = useLocation();
  
  // Check URL hash for session_id (OAuth callback) - MUST be synchronous, not in useEffect
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback setUser={setUser} />;
  }
  
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/license-agreement" element={<LicenseAgreement />} />
      <Route path="/sales-pitch" element={<SalesPitch />} />
      <Route path="/support-admin" element={<SupportAdminPortal />} />
      <Route path="/documentation" element={<DocumentationPage />} />
      <Route path="/login" element={<LoginPage setUser={setUser} />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/signup" element={<CompanyOnboarding setUser={setUser} />} />
      <Route path="/activate/:token" element={<ActivateAccount setUser={setUser} />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/verify-email/:token" element={<EmailVerified setUser={setUser} />} />
      <Route path="/email-verified" element={<EmailVerified setUser={setUser} />} />
      <Route path="/auth/callback" element={<AuthCallback setUser={setUser} />} />
      <Route path="/sign/:documentId/:token" element={<PublicSign />} />
      <Route
        path="/dashboard/*"
        element={
          <PrivateRoute>
            <DashboardLayout user={user}>
              <Routes>
                <Route path="/" element={<Dashboard />} />
                <Route path="/employees" element={<Employees />} />
                <Route path="/employees/:id" element={<EmployeeDetails />} />
                <Route path="/employees/:id/edit" element={<EmployeeEdit />} />
                <Route path="/my-profile" element={<MyProfile />} />
                <Route path="/my-documents" element={<MyDocuments />} />
                <Route path="/leave" element={<LeaveManagement />} />
                <Route path="/attendance" element={<Attendance />} />
                <Route path="/claims" element={<Claims />} />
                <Route path="/payroll" element={<Payroll />} />
                <Route path="/payroll/:employeeId" element={<PayrollDetail />} />
                <Route path="/payslips" element={<Payslips />} />
                <Route path="/performance" element={<Performance />} />
                <Route path="/reports" element={<Reports />} />
                <Route path="/forms" element={<Forms />} />
                <Route path="/settings" element={<Settings />} />
                {/* Iter86 — White-label Branding (company logo / colors)
                    for payslip-level customization. Was imported but
                    never routed. */}
                <Route path="/branding" element={<BrandingSettings />} />
                <Route path="/employee-settings" element={<EmployeeSettings />} />
                <Route path="/my-team" element={<MyTeam />} />
                <Route path="/import" element={<ImportData />} />
                <Route path="/documents" element={<Documents />} />
                <Route path="/license-support" element={<LicenseSupport />} />
                <Route path="/license-admin" element={<LicenseAdmin />} />
                <Route path="/license-management" element={<LicenseManagement />} />
                <Route path="/ai-config" element={<AIConfiguration />} />
                <Route path="/migration" element={<DataMigration />} />
                <Route path="/pscl-admin" element={<PSCLAdmin />} />
                <Route path="/hr-support" element={<HRSupportChat />} />
                <Route path="/hr-inbox" element={<HRInbox />} />
                <Route path="/overtime" element={<OvertimeManagement />} />
                <Route path="/probation" element={<ProbationManagement />} />
                <Route path="/training" element={<TrainingComingSoon />} />
                <Route path="/org-chart" element={<OrgChartComingSoon />} />
                {/* Iter49 — full notifications page */}
                <Route path="/notifications" element={<Notifications />} />
              </Routes>
            </DashboardLayout>
          </PrivateRoute>
        }
      />
    </Routes>
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasPreviewAccess, setHasPreviewAccess] = useState(false);
  const ssoStatus = useSSOHandler();

  useEffect(() => {
    // Check for SSO token in URL - if present, skip preview check
    const hash = window.location.hash;
    const urlParams = new URLSearchParams(window.location.search);
    const hasSSO = hash.includes('sso_token=') || urlParams.has('sso_token');
    
    // Allow public signing page without password protection
    const isPublicSigningPage = window.location.pathname.startsWith('/sign/');
    const isLicenseAgreementPage = window.location.pathname === '/license-agreement';
    const isSalesPitchPage = window.location.pathname === '/sales-pitch';
    const isSupportAdminPage = window.location.pathname === '/support-admin';
    
    if (hasSSO || localStorage.getItem('sso_authenticated') === 'true' || isPublicSigningPage || isLicenseAgreementPage || isSalesPitchPage || isSupportAdminPage) {
      // SSO token present, user already authenticated via SSO, public signing page, license agreement, sales pitch, or support admin
      setHasPreviewAccess(true);
    } else {
      // Check preview access normally
      setHasPreviewAccess(checkPreviewAccess());
    }
    
    // TC-AUTH-033: Check both localStorage and sessionStorage for auth
    const { token, user: storedUser } = getAuthStorage();
    
    if (token && storedUser) {
      setUser(JSON.parse(storedUser));
    }
    setLoading(false);
  }, []);

  // Show SSO processing state
  if (ssoStatus === 'processing') {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#0f172a',
        color: 'white'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: 48,
            height: 48,
            border: '4px solid #10b981',
            borderTopColor: 'transparent',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }} />
          <p style={{ fontSize: '18px', marginBottom: '8px' }}>Signing you in via SSO...</p>
          <p style={{ fontSize: '14px', color: '#94a3b8' }}>Please wait while we verify your credentials</p>
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  // Show password protection screen if no access (skip for SSO users)
  if (!hasPreviewAccess && ssoStatus !== 'success') {
    return (
      <ThemeProvider>
        <PasswordProtection onAccessGranted={() => setHasPreviewAccess(true)} />
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider>
      <LanguageProvider>
        <div className="App">
          <Toaster position="bottom-center" richColors />
          <BrowserRouter>
            <AppRouter user={user} setUser={setUser} />
            {/* Show chatbot on public pages only */}
            <ChatbotWrapper />
          </BrowserRouter>
        </div>
      </LanguageProvider>
    </ThemeProvider>
  );
}

// Wrapper to conditionally show chatbot only on public pages
function ChatbotWrapper() {
  const location = useLocation();
  const publicPaths = ['/', '/login', '/signup', '/license-agreement'];
  const isPublicPage = publicPaths.includes(location.pathname) || location.pathname.startsWith('/docs') || location.pathname.startsWith('/documentation');
  
  if (!isPublicPage) return null;
  return <ChatbotWidget />;
}

export default App;
export { API };